import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';

const Section = styled.section`
  background: #fff;
  padding: 100px 0;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 64px 0;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 60px;

  .tag {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #f08020;
    margin-bottom: 14px;
  }

  h2 {
    font-size: 2.2rem;
    font-weight: 700;
    color: #111;
    line-height: 1.2;
    display: inline-block;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 80px;
      height: 2px;
      background: #f08020;
    }

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
`;

const Viewport = styled.div`
  position: relative;
  overflow: hidden;
  margin: 0 auto;
`;

const Track = styled.div`
  display: flex;
  transition: transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
  transform: translateX(${({ offset }) => offset}%);
`;

const LogoItem = styled.div`
  flex: 0 0 ${({ widthPct }) => widthPct}%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 140px;
  padding: 0 24px;

  @media (max-width: 768px) {
    height: 100px;
    padding: 0 16px;
  }

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 40px;

  @media (max-width: 768px) {
    margin-top: 28px;
  }
`;

const Dot = styled.button`
  width: ${({ active }) => (active ? '28px' : '8px')};
  height: 8px;
  border-radius: 4px;
  border: none;
  background: ${({ active }) =>
    active ? '#F08020' : 'rgba(19, 120, 229, 0.18)'};
  cursor: pointer;
  padding: 0;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background: ${({ active }) =>
      active ? '#F08020' : 'rgba(19, 120, 229, 0.4)'};
  }
`;

const CtaWrap = styled.div`
  text-align: center;
  margin-top: 48px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #fff;
    background: #f08020;
    padding: 14px 36px;
    position: relative;
    overflow: hidden;
    transition: color 0.4s ease, background 0.4s ease;
    z-index: 1;

    svg {
      width: 14px;
      height: 14px;
      stroke: #fff;
      fill: none;
      stroke-width: 1.5;
      stroke-linecap: round;
      stroke-linejoin: round;
      transition: transform 0.3s ease;
    }

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: #f4a040;
      transform: translateX(-101%);
      transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      z-index: -1;
    }

    &:hover {
      &::before {
        transform: translateX(0);
      }
      svg {
        transform: translateX(4px);
      }
    }
  }
`;

function useVisibleCount() {
  const [count, setCount] = useState(5);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 540) setCount(2);
      else if (w < 900) setCount(3);
      else if (w < 1200) setCount(4);
      else setCount(5);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return count;
}

export default function Awards({ tag, title, ctaLabel, ctaLink, logos }) {
  const data = logos || [];
  const visible = useVisibleCount();
  const [current, setCurrent] = useState(0);

  const total = data.length;
  const maxIndex = Math.max(0, total - visible);

  const goNext = useCallback(() => {
    setCurrent(prev => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  // Auto advance one item at a time
  useEffect(() => {
    if (total <= visible) return;
    const timer = setInterval(goNext, 3000);
    return () => clearInterval(timer);
  }, [goNext, total, visible]);

  // Reset current if it goes out of bounds (e.g., resize)
  useEffect(() => {
    if (current > maxIndex) setCurrent(0);
  }, [current, maxIndex]);

  if (!data.length && !title) return null;

  const widthPct = 100 / visible;
  const offset = -(current * widthPct);

  return (
    <Section>
      <Container>
        <Header>
          {tag && <div className="tag">{tag}</div>}
          {title && <h2>{title}</h2>}
        </Header>

        {data.length > 0 && (
          <>
            <Viewport>
              <Track offset={offset}>
                {data.map((logo, idx) => (
                  <LogoItem key={idx} widthPct={widthPct}>
                    {logo?.asset?.url && (
                      <img
                        src={logo.asset.url + '?w=400&auto=format'}
                        alt={`Award ${idx + 1}`}
                        loading="lazy"
                      />
                    )}
                  </LogoItem>
                ))}
              </Track>
            </Viewport>

            {total > visible && (
              <Dots>
                {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
                  <Dot
                    key={idx}
                    active={idx === current}
                    onClick={() => setCurrent(idx)}
                    aria-label={`Go to position ${idx + 1}`}
                  />
                ))}
              </Dots>
            )}
          </>
        )}

        {ctaLabel && (
          <CtaWrap>
            <Link to={ctaLink || '/contact'}>
              {ctaLabel}
              <svg viewBox="0 0 24 24">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </CtaWrap>
        )}
      </Container>
    </Section>
  );
}
